'use client';

/**
 * RecordingMiniBar
 *
 * In-flow footer bar rendered in the root layout. Only reads from Zustand
 * stores (no React context providers needed). Shows recording status,
 * timer, and controls when the full-screen modal is minimised.
 */

import { useState, useLayoutEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ChevronUp, AudioLines, Pause, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RECORDING_MINI_BAR_HEIGHT_PX } from '@/lib/constants';
import { formatTimer } from '@/lib/services/recordings';
import {
  useRecordingSessionActions,
  useIsRecording,
  useIsMinimised,
  useIsPaused,
  useElapsedSeconds,
  useRecordingMatterId,
  useRecordingMatterName,
  useRecordingMatterReference
} from '@/lib/stores';
import { useRecordingWaveformStore } from '@/lib/stores/recording-waveform-store';

export function RecordingMiniBar() {
  const isRecording = useIsRecording();
  const isMinimised = useIsMinimised();

  // Don't render if not recording or not minimised
  if (!isRecording || !isMinimised) return null;

  return <RecordingMiniBarBody />;
}

function RecordingMiniBarBody() {
  const router = useRouter();
  const pathname = usePathname();

  const isPaused = useIsPaused();
  const elapsedSeconds = useElapsedSeconds();
  const matterId = useRecordingMatterId();
  const matterName = useRecordingMatterName();
  const matterReference = useRecordingMatterReference();

  const actions = useRecordingSessionActions();
  const frequencyData = useRecordingWaveformStore((s) => s.frequencyData);

  // Intro animation: mount at scaleY(0), then reveal after the modal out-transition
  const [revealed, setRevealed] = useState(false);

  useLayoutEffect(() => {
    const timer = setTimeout(() => setRevealed(true), 400);
    return () => clearTimeout(timer);
  }, []);

  // Check if user is on a different page than the recording's matter
  const _isOnDifferentPage = matterId
    ? !pathname?.includes(`/matters/${matterId}`)
    : false;

  const formattedTime = formatTimer(elapsedSeconds);

  const handleNavigateToMatter = () => {
    if (matterId) {
      router.push(`/matters/${matterId}/recordings`);
    }
  };

  return (
    <div
      className={cn(
        // A permanently dark region (VALE-525 · DARK-5a) — see
        // `recording-modal.tsx` for why `dark` is a scope here rather than the
        // user's theme preference.
        'dark border-foreground bg-card fixed inset-x-0 bottom-0 z-50 origin-bottom border-t px-4 py-2.5 transition-transform duration-300 ease-out',
        revealed ? '[transform:scaleY(1)]' : '[transform:scaleY(0)]'
      )}
      // Set from the shared constant rather than a `h-[55px]` class: the toaster
      // lifts its stack by this exact height to clear the controls below, and a
      // Tailwind arbitrary value can't be driven from a constant, so the two
      // would silently drift apart (LF-6129).
      style={{ height: RECORDING_MINI_BAR_HEIGHT_PX }}
    >
      <div className="flex items-center gap-3">
        {/* Status indicator */}
        {isPaused ? (
          <div className="flex items-center gap-2">
            <Pause className="text-foreground/40 h-3 w-3" />
            <span className="text-foreground/40 text-xs font-medium">
              PAUSED
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="bg-error h-2 w-2 animate-pulse rounded-full" />
            <span className="text-error text-xs font-medium">REC</span>
          </div>
        )}

        {/* Timer */}
        <span
          className={cn(
            'font-mono text-sm font-semibold tabular-nums',
            isPaused ? 'text-foreground/40' : 'text-foreground'
          )}
        >
          {formattedTime}
        </span>

        {/* Waveform bars */}
        <div className="flex h-5 items-center gap-0.5">
          {Array.from({ length: 12 }, (_, i) => {
            const value =
              !isPaused && frequencyData
                ? Math.max(
                    3,
                    (frequencyData[i % frequencyData.length] / 255) * 20
                  )
                : 3;
            return (
              <div
                key={i}
                className={cn(
                  'w-[2px] rounded-full transition-all duration-75',
                  isPaused ? 'bg-foreground/10' : 'bg-success'
                )}
                style={{ height: `${value}px` }}
              />
            );
          })}
        </div>

        {/* Matter context — always visible, always links to matter */}
        {!!(matterName || matterReference) && (
          <>
            <span className="text-foreground/20">&middot;</span>
            <button
              type="button"
              onClick={handleNavigateToMatter}
              className="text-info hover:text-foreground truncate text-xs"
            >
              {matterName ? `Estate of ${matterName}` : matterReference}
            </button>
          </>
        )}

        {/* Expand button */}
        <button
          type="button"
          onClick={actions.maximise}
          className="text-foreground/50 hover:text-foreground/70 ml-auto flex items-center gap-1 text-xs"
        >
          <ChevronUp className="h-3 w-3" />
        </button>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {isPaused ? (
            <button
              type="button"
              onClick={actions.resume}
              className="bg-foreground text-background hover:bg-foreground/90 flex h-8 w-8 items-center justify-center rounded-full"
            >
              <AudioLines className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={actions.pause}
              className="bg-foreground/10 text-foreground hover:bg-foreground/15 flex h-8 w-8 items-center justify-center rounded-full"
            >
              <Pause className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              actions.setRequestStopConfirm();
              actions.maximise();
            }}
            className="bg-error text-error-foreground hover:bg-error/90 flex h-8 w-8 items-center justify-center rounded-full"
          >
            <Square className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
